import React, { useEffect, useMemo, useRef, useState } from 'react';

export type MultiSelectOption = {
    value: number | string;
    label: string;
    hint?: string;
};

/**
 * Dropdown multichoice — zamjenjuje duge checkbox liste u admin sekcijama.
 * - Klik na header otvara panel s opcijama
 * - Quick search po label-u
 * - "Sve / Ništa" toggle
 * - Klik izvan ili Esc zatvara
 *
 * Vrijednost: list<value>. Kontrolirana komponenta — parent drži `selected` i `onChange`.
 */
export function MultiSelectDropdown({
    label,
    options,
    selected,
    onChange,
    placeholder,
    emptyLabel,
    locale,
}: {
    label?: string;
    options: MultiSelectOption[];
    selected: Array<number | string>;
    onChange: (next: Array<number | string>) => void;
    placeholder?: string;
    emptyLabel?: string;
    locale?: 'hr' | 'en';
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const rootRef = useRef<HTMLDivElement | null>(null);
    const lang = locale ?? 'hr';

    useEffect(() => {
        if (!open) return;
        const handler = (ev: MouseEvent | KeyboardEvent) => {
            if (ev instanceof KeyboardEvent) {
                if (ev.key === 'Escape') setOpen(false);
                return;
            }
            if (rootRef.current && !rootRef.current.contains(ev.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler as EventListener);
        document.addEventListener('keydown', handler as EventListener);
        return () => {
            document.removeEventListener('mousedown', handler as EventListener);
            document.removeEventListener('keydown', handler as EventListener);
        };
    }, [open]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q));
    }, [options, search]);

    const selectedSet = useMemo(() => new Set(selected.map(String)), [selected]);

    const toggle = (value: number | string) => {
        const key = String(value);
        if (selectedSet.has(key)) {
            onChange(selected.filter((v) => String(v) !== key));
        } else {
            onChange([...selected, value]);
        }
    };

    const allOnPage = filtered.every((o) => selectedSet.has(String(o.value)));
    const noneOnPage = filtered.every((o) => !selectedSet.has(String(o.value)));

    const summary = (() => {
        if (selected.length === 0) {
            return emptyLabel ?? (lang === 'en' ? 'None selected' : 'Odaberi…');
        }
        if (selected.length <= 3) {
            return options.filter((o) => selectedSet.has(String(o.value))).map((o) => o.label).join(', ');
        }
        return lang === 'en'
            ? `${selected.length} selected`
            : `Odabrano: ${selected.length}`;
    })();

    return (
        <div ref={rootRef} className="multi-select-dropdown" style={{ position: 'relative', width: '100%' }}>
            {label ? <label className="filter-label" style={{ marginBottom: 4, display: 'block' }}>{label}</label> : null}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="secondary"
                style={{
                    width: '100%', textAlign: 'left',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                    padding: '0.55rem 0.75rem',
                }}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
                <span aria-hidden="true">{open ? '▴' : '▾'}</span>
            </button>
            {open ? (
                <div
                    role="listbox"
                    className="multi-select-panel"
                    style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 30,
                        background: 'var(--surface, #fff)',
                        border: '1px solid rgba(var(--ad-soft-rgb),0.32)',
                        borderRadius: 10, boxShadow: '0 8px 22px rgba(0,0,0,0.14)',
                        padding: 8, maxHeight: 320, overflowY: 'auto',
                    }}
                >
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                        <input
                            type="text"
                            value={search}
                            onChange={(ev) => setSearch(ev.target.value)}
                            placeholder={placeholder ?? (lang === 'en' ? 'Search…' : 'Pretraga…')}
                            style={{ flex: 1, padding: '0.4rem 0.55rem' }}
                            autoFocus
                        />
                        <button
                            type="button"
                            className="secondary compact"
                            onClick={() => onChange(allOnPage ? selected.filter((v) => !filtered.some((o) => String(o.value) === String(v))) : Array.from(new Set([...selected, ...filtered.map((o) => o.value)])))}
                            title={allOnPage ? (lang === 'en' ? 'Deselect filtered' : 'Odznači sve filtrirano') : (lang === 'en' ? 'Select filtered' : 'Označi sve filtrirano')}
                        >
                            {allOnPage ? (lang === 'en' ? 'None' : 'Ništa') : (lang === 'en' ? 'All' : 'Sve')}
                        </button>
                    </div>
                    {filtered.length === 0 ? (
                        <p className="muted" style={{ margin: '0.4rem 0' }}>
                            {lang === 'en' ? 'No matches.' : 'Nema rezultata.'}
                        </p>
                    ) : (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {filtered.map((o) => {
                                const checked = selectedSet.has(String(o.value));
                                return (
                                    <li key={String(o.value)}>
                                        <label
                                            className="multi-select-item"
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 8,
                                                padding: '0.4rem 0.55rem', borderRadius: 6, cursor: 'pointer',
                                                background: checked ? 'rgba(var(--ad-acc-rgb), 0.12)' : 'transparent',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggle(o.value)}
                                            />
                                            <span style={{ flex: 1 }}>{o.label}</span>
                                            {o.hint ? <span className="muted" style={{ fontSize: '0.8rem' }}>{o.hint}</span> : null}
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                        <span className="muted" style={{ fontSize: '0.8rem' }}>
                            {lang === 'en' ? `${selected.length} selected` : `Odabrano: ${selected.length}`}
                        </span>
                        <button type="button" className="secondary compact" onClick={() => setOpen(false)}>
                            {lang === 'en' ? 'Done' : 'Gotovo'}
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
